// Utility functions for creating SEO-friendly URL slugs

/**
 * Converts a blog post title to a URL-friendly slug
 * @param title - The blog post title
 * @returns A URL-friendly slug
 */
const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    // Replace German umlauts
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    // Remove special characters and replace with hyphens
    .replace(/[^a-z0-9\s-]/g, '')
    // Replace spaces and multiple hyphens with single hyphen
    .replace(/[\s-]+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '');
};

/**
 * Creates a unique slug by appending the blog post ID
 * @param title - The blog post title
 * @param id - The blog post ID
 * @returns A unique URL-friendly slug
 */
export const createUniqueSlug = (title: string, id: string): string => {
  const baseSlug = createSlug(title);
  // Take first 8 characters of ID for uniqueness while keeping URLs readable
  const shortId = id.substring(0, 8);
  return `${baseSlug}-${shortId}`;
};

/**
 * Extracts the blog post ID from a slug
 * @param slug - The URL slug
 * @returns The extracted blog post ID or null if not found
 */
const extractIdFromSlug = (slug: string): string | null => {
  // Extract the last part after the final hyphen (should be the short ID)
  const parts = slug.split('-');
  if (parts.length < 2) return null;
  
  const shortId = parts[parts.length - 1];
  if (shortId.length !== 8) return null;
  
  return shortId;
};

/**
 * Finds a blog post by slug
 * @param posts - Array of blog posts
 * @param slug - The URL slug
 * @returns The matching blog post or undefined
 */
export const findPostBySlug = (posts: any[], slug: string): any | undefined => {
  const shortId = extractIdFromSlug(slug);
  if (!shortId) return undefined;

  return posts.find(post => post.id.startsWith(shortId));
};

/**
 * Builds an SEO-friendly slug for a doctor record.
 * Format: <kebab-name>-<8charId>. Falls back to bare id when no name is available.
 */
export const createDoctorSlug = (doctor: { id: string; provider_name?: string | null; first_name?: string | null; last_name?: string | null; city?: string | null }): string => {
  const namePart = doctor.provider_name
    || [doctor.first_name, doctor.last_name].filter(Boolean).join(' ').trim()
    || '';
  if (!namePart) return doctor.id;
  const base = createSlug(namePart);
  if (!base) return doctor.id;
  // Optional city suffix improves uniqueness for very common names without bloating
  const cityPart = doctor.city ? createSlug(doctor.city) : '';
  const shortId = doctor.id.substring(0, 8);
  return cityPart ? `${base}-${cityPart}-${shortId}` : `${base}-${shortId}`;
};

/**
 * Tries to recover a doctor id from either a full UUID slug, an id-suffixed
 * SEO slug, or a raw UUID.
 */
export const findDoctorBySlug = (doctors: { id: string }[], slug: string): { id: string } | undefined => {
  if (!slug) return undefined;
  // Direct UUID match (legacy URLs)
  const direct = doctors.find((d) => d.id === slug);
  if (direct) return direct;
  // SEO slug: last hyphen-segment is the 8-char id prefix
  const parts = slug.split('-');
  const shortId = parts[parts.length - 1];
  if (shortId && shortId.length === 8) {
    return doctors.find((d) => d.id.startsWith(shortId));
  }
  return undefined;
};